import type { PointCloudOctree } from '../PointCloudOctree';
import type { PointCloudMaterial } from '../materials/PointCloudMaterial';
import type { MaterialOverride, ClipOverride } from './RenderView';
import type { ClipState } from './buildClipState';
import { applyClipState } from './buildClipState';

/**
 * Snapshot of fields we may overwrite on a single PointCloudMaterial.
 *
 * Fields are optional — only snapshotted fields get restored, so applying
 * an override that doesn't touch `useEDL` won't create a fake snapshot of
 * `useEDL` that then re-writes the field on restore (silent corruption).
 */
interface MaterialSnapshot {
  material: PointCloudMaterial;
  activeAttributeName?: string | null;
  useEDL?: boolean;
  size?: number;
  shape?: number;
  densityMode?: boolean;
}

/**
 * Snapshot of the clip-state-driven fields on a material. We snapshot the
 * arrays themselves (the setters replace them wholesale, so holding the old
 * reference is enough to restore).
 */
interface ClipSnapshot {
  material: PointCloudMaterial;
  clipBoxes: PointCloudMaterial['clipBoxes'];
  clipPolygons: PointCloudMaterial['clipPolygons'];
  clipPlanes: PointCloudMaterial['clipPlanes'];
  clusteredSegments: PointCloudMaterial['clusteredSegments'];
}

/**
 * Apply per-view material overrides + clip override around a render call.
 *
 * Usage:
 *   const restore = applyOverrides(pointclouds, view.materialOverride, view.clipOverride, baseClipState, maxPolygonVertices);
 *   try { pipeline.render(ctx, state); } finally { restore(); }
 *
 * The try/finally is mandatory — leaking overrides corrupts the next view's
 * render (or the main view's next frame) because PointCloudMaterial fields
 * are shared across all views.
 */
export function applyOverrides(
  pointclouds: PointCloudOctree[],
  material: MaterialOverride,
  clip: ClipOverride | null,
  baseClipState: ClipState,
  maxPolygonVertices: number,
): () => void {
  const matSnapshots: MaterialSnapshot[] = [];
  const clipSnapshots: ClipSnapshot[] = [];

  // 1. Material-field overrides. Snapshot then write each specified field.
  const hasMatOverride = material.activeAttributeName !== undefined || material.useEDL !== undefined || material.size !== undefined || material.shape !== undefined || material.densityMode !== undefined;
  if (hasMatOverride) {
    for (const pc of pointclouds) {
      const m = pc.material as unknown as PointCloudMaterial & { size?: number; shape?: number; densityMode?: boolean };
      const snap: MaterialSnapshot = { material: m };
      if (material.activeAttributeName !== undefined) {
        snap.activeAttributeName = m.activeAttributeName;
        m.activeAttributeName = material.activeAttributeName;
      }
      if (material.useEDL !== undefined) {
        snap.useEDL = (m as unknown as { useEDL: boolean }).useEDL;
        (m as unknown as { useEDL: boolean }).useEDL = material.useEDL;
      }
      if (material.size !== undefined) {
        snap.size = m.size;
        m.size = material.size;
      }
      if (material.shape !== undefined) {
        snap.shape = m.shape;
        m.shape = material.shape;
      }
      if (material.densityMode !== undefined) {
        snap.densityMode = m.densityMode;
        m.densityMode = material.densityMode;
      }
      matSnapshots.push(snap);
    }
  }

  // 2. Clip overrides. The setters re-run shader generation when counts
  // change, so prefer using the same shapes as baseClipState when possible.
  if (clip !== null) {
    for (const pc of pointclouds) {
      const m = pc.material as unknown as PointCloudMaterial;
      clipSnapshots.push({
        material: m,
        clipBoxes: m.clipBoxes,
        clipPolygons: m.clipPolygons,
        clipPlanes: m.clipPlanes,
        clusteredSegments: m.clusteredSegments,
      });
    }

    if (clip.mode === 'none') {
      const empty: ClipState = {
        clipBoxes: [],
        clipPolygons: [],
        clipPlanes: [],
        clusteredSegmentsByPointCloud: new Map(),
      };
      applyClipState(pointclouds, empty, maxPolygonVertices);
    } else {
      applyClipState(pointclouds, clip.state, maxPolygonVertices);
    }
  }

  // The base clip state is already on the materials when the caller is the
  // main view (no override). Nothing to do for `clip === null`.
  void baseClipState;

  return function restore() {
    // Restore in reverse order: clip first (it shadows the most), then materials.
    if (clipSnapshots.length > 0) {
      for (const snap of clipSnapshots) {
        snap.material.setClipBoxes(snap.clipBoxes);
        snap.material.setClipPolygons(snap.clipPolygons);
        snap.material.setClipPlanes(snap.clipPlanes);
        snap.material.setClusteredSegments(snap.clusteredSegments);
      }
    }
    for (const snap of matSnapshots) {
      const m = snap.material as unknown as PointCloudMaterial & { size?: number; shape?: number; densityMode?: boolean };
      if (snap.activeAttributeName !== undefined) m.activeAttributeName = snap.activeAttributeName;
      if (snap.useEDL !== undefined) (m as unknown as { useEDL: boolean }).useEDL = snap.useEDL;
      if (snap.size !== undefined) m.size = snap.size;
      if (snap.shape !== undefined) m.shape = snap.shape;
      if (snap.densityMode !== undefined) m.densityMode = snap.densityMode;
    }
  };
}
